import React, { useState, useRef, useEffect } from 'react';
import { Layers, Move, MousePointer2, Trash2, Check, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';

const LayoutDesigner = ({ width = 800, height = 1100, onSave, onCancel }) => {
    // Initialize with Background Panel
    const [panels, setPanels] = useState([
        {
            id: 'p-custom-bg',
            x: 0,
            y: 0,
            width: width,
            height: height,
            color: '#ffffff', // White background
            points: [{ x: 0, y: 0 }, { x: 100, y: 0 }, { x: 100, y: 100 }, { x: 0, y: 100 }],
            clipPath: 'none',
            zIndex: 0,
            locked: true,
            isBackground: true
        }
    ]);

    // Update background panel size when props change
    useEffect(() => {
        setPanels(prev => prev.map(p =>
            p.isBackground ? { ...p, width: width, height: height } : p
        ));
    }, [width, height]);

    // [NEW] Auto-Scale Logic
    const [scale, setScale] = useState(1);
    const canvasContainerRef = useRef(null); // The wrapper (gray background)

    useEffect(() => {
        const handleResize = () => {
            if (canvasContainerRef.current) {
                const { clientWidth, clientHeight } = canvasContainerRef.current;
                // Add 40px padding calculation (p-8 = 32px roughly)
                const availableW = clientWidth - 64;
                const availableH = clientHeight - 64;

                const scaleW = availableW / width;
                const scaleH = availableH / height;

                // Use the smaller scale to fit entirely, with a max of 1
                const newScale = Math.min(scaleW, scaleH, 1) * 0.95;
                setScale(newScale);
            }
        };

        handleResize(); // Initial calc
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [width, height]);

    const [selectedId, setSelectedId] = useState(null);
    const [mode, setMode] = useState('draw'); // 'draw' | 'select' | 'edit-points'
    const [isDrawing, setIsDrawing] = useState(false);
    const [drawStart, setDrawStart] = useState(null);
    const [currentRect, setCurrentRect] = useState(null);

    // For dragging points
    const [draggingPoint, setDraggingPoint] = useState(null); // { panelId, pointIndex }

    // For dragging panels
    const [draggingPanel, setDraggingPanel] = useState(null); // { id, startX, startY, initialPanelX, initialPanelY }

    const containerRef = useRef(null);

    // Coordinate conversion: Screen -> Percent
    const toPercent = (val, max) => (val / max) * 100;
    const toPx = (val, max) => (val / 100) * max;

    // Handle Mouse Down on Canvas
    const handleMouseDown = (e) => {
        // Use clientX/Y relative to the element, but adjust for scale
        const rect = containerRef.current.getBoundingClientRect();
        // Calculate X/Y inside the scaled element
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;

        if (mode === 'draw') {
            setIsDrawing(true);
            setDrawStart({ x, y });
            setCurrentRect({ x, y, w: 0, h: 0 });
            setSelectedId(null);
        } else if (mode === 'select') {
            // Deselect if clicking empty space
            if (e.target === containerRef.current) {
                setSelectedId(null);
            }
        } else if (mode === 'edit-points') {
            // If clicking background in edit-points mode, deselect
            if (e.target === containerRef.current) {
                setSelectedId(null);
            }
        }
    };

    // Handle Mouse Move
    const handleMouseMove = (e) => {
        const rect = containerRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;

        if (isDrawing && drawStart) {
            const w = x - drawStart.x;
            const h = y - drawStart.y;
            setCurrentRect({
                x: w > 0 ? drawStart.x : x,
                y: h > 0 ? drawStart.y : y,
                w: Math.abs(w),
                h: Math.abs(h)
            });
        }



        if (draggingPanel) {
            const dx = x - draggingPanel.startX;
            const dy = y - draggingPanel.startY;

            setPanels(prev => prev.map(p => {
                if (p.id === draggingPanel.id) {
                    return {
                        ...p,
                        x: draggingPanel.initialPanelX + dx,
                        y: draggingPanel.initialPanelY + dy
                    };
                }
                return p;
            }));
        }

        if (draggingPoint) {
            const { panelId, pointIndex } = draggingPoint;
            const panel = panels.find(p => p.id === panelId);
            if (panel) {
                // Calculate point relative to panel bounds (0-100%)
                const relX = x - panel.x;
                const relY = y - panel.y;

                // Convert to percentage
                const px = Math.max(0, Math.min(100, (relX / panel.width) * 100));
                const py = Math.max(0, Math.min(100, (relY / panel.height) * 100));

                setPanels(prev => prev.map(p => {
                    if (p.id === panelId) {
                        const newPoints = [...p.points];
                        newPoints[pointIndex] = { x: px, y: py };
                        // Update clipPath
                        const clipPath = `polygon(${newPoints.map(pt => `${pt.x}% ${pt.y}%`).join(', ')})`;
                        return { ...p, points: newPoints, clipPath };
                    }
                    return p;
                }));
            }
        }
    };

    // Handle Mouse Up
    const handleMouseUp = () => {
        if (isDrawing && currentRect) {
            if (currentRect.w > 20 && currentRect.h > 20) {
                const newId = `p-custom-${panels.length + 1}`;
                const newPanel = {
                    id: newId,
                    x: currentRect.x,
                    y: currentRect.y,
                    width: currentRect.w,
                    height: currentRect.h,
                    color: `hsl(${Math.random() * 360}, 70%, 85%)`,
                    // Default Points (Rectangle)
                    points: [
                        { x: 0, y: 0 },   // Top-Left
                        { x: 100, y: 0 }, // Top-Right
                        { x: 100, y: 100 }, // Bottom-Right
                        { x: 0, y: 100 }  // Bottom-Left
                    ],
                    clipPath: 'none',
                    zIndex: 10 + panels.length // Ensure it floats above background
                };
                setPanels([...panels, newPanel]);
                setSelectedId(newId);
                setMode('select'); // Auto-switch to select so they can edit
            }
            setIsDrawing(false);
            setDrawStart(null);
            setCurrentRect(null);
        }
        setDraggingPoint(null);
        setDraggingPanel(null);
    };

    // Delete Panel
    const handleDelete = () => {
        if (selectedId) {
            const panel = panels.find(p => p.id === selectedId);
            if (panel && (panel.locked || panel.isBackground)) {
                toast.error("Cannot delete the background panel");
                return;
            }
            setPanels(panels.filter(p => p.id !== selectedId));
            setSelectedId(null);
        }
    };

    // Handle Save
    const handleSave = () => {
        if (panels.length === 0) {
            toast.error("Please draw at least one panel");
            return;
        }
        onSave(panels);
    };

    return (
        <div className="flex flex-col h-full bg-gray-950 p-6 rounded-xl border border-gray-800 shadow-2xl relative">
            {/* Toolbar */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-gray-900 border border-gray-700 rounded-full p-2 flex gap-2 shadow-xl z-50">
                <Button
                    variant={mode === 'draw' ? 'default' : 'ghost'} // Removed 'default' string literal usage error potential if UI lib uses specific strings
                    className={mode === 'draw' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}
                    size="sm"
                    onClick={() => setMode('draw')}
                    title="Draw Rectangle"
                >
                    <Layers className="w-4 h-4 mr-2" /> Draw
                </Button>
                <Button
                    variant={mode === 'select' ? 'default' : 'ghost'}
                    className={mode === 'select' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}
                    size="sm"
                    onClick={() => setMode('select')}
                    title="Select & Move"
                >
                    <Move className="w-4 h-4 mr-2" /> Select
                </Button>
                <Button
                    variant={mode === 'edit-points' ? 'default' : 'ghost'}
                    className={mode === 'edit-points' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}
                    size="sm"
                    onClick={() => setMode('edit-points')}
                    disabled={!selectedId}
                    title="Edit Corner Points (Skew)"
                >
                    <MousePointer2 className="w-4 h-4 mr-2" /> Skew Points
                </Button>

                <div className="w-px h-8 bg-gray-700 mx-2" />

                <Button
                    variant="destructive"
                    size="icon"
                    onClick={handleDelete}
                    disabled={!selectedId}
                    title="Delete Selected"
                    className="bg-red-900/50 hover:bg-red-700 text-red-200"
                >
                    <Trash2 className="w-4 h-4" />
                </Button>
            </div>

            {/* Canvas Area with Auto-Scale Wrapper */}
            <div ref={canvasContainerRef} className="flex-1 overflow-hidden flex items-center justify-center p-4 bg-gray-900/50 rounded-lg border border-dashed border-gray-800">
                <div
                    ref={containerRef}
                    className="relative bg-white shadow-2xl cursor-crosshair transition-transform duration-200 ease-out"
                    style={{
                        width: width,
                        height: height,
                        transform: `scale(${scale})`,
                        transformOrigin: 'center center',
                        cursor: mode === 'draw' ? 'crosshair' : 'default'
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    {/* Grid Lines (Visual Aid) */}
                    <div className="absolute inset-0 pointer-events-none opacity-10"
                        style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '50px 50px' }}
                    />

                    {/* Panels */}
                    {panels.map((panel) => (
                        <div
                            key={panel.id}
                            onMouseDown={(e) => {
                                if (mode === 'select' || mode === 'edit-points') {
                                    e.stopPropagation();
                                    // Don't allow selecting background in edit-points mode if we want to prevent skewing it
                                    // But user might want to skew background? Let's allow it but maybe warn?
                                    // For now, allow selection but we might want to lock it from moving if it's background
                                    setSelectedId(panel.id);

                                    // Initiate Drag if in Select Mode and not locked
                                    if (mode === 'select' && !panel.locked && !panel.isBackground) {
                                        const rect = containerRef.current.getBoundingClientRect();
                                        const mouseX = (e.clientX - rect.left) / scale;
                                        const mouseY = (e.clientY - rect.top) / scale;

                                        setDraggingPanel({
                                            id: panel.id,
                                            startX: mouseX,
                                            startY: mouseY,
                                            initialPanelX: panel.x,
                                            initialPanelY: panel.y
                                        });
                                    }
                                }
                            }}
                            className={`absolute transition-colors ${selectedId === panel.id ? 'ring-2 ring-purple-600 z-50' : ''} ${panel.isBackground ? '' : 'hover:ring-1 hover:ring-purple-400/50'} ${draggingPanel?.id === panel.id ? 'cursor-grabbing' : ''}`}
                            style={{
                                left: panel.x,
                                top: panel.y,
                                width: panel.width,
                                height: panel.height,
                                backgroundColor: panel.color,
                                clipPath: panel.clipPath !== 'none' ? panel.clipPath : undefined,
                                zIndex: panel.zIndex || 0,
                                // If clipPath exists, we need a way to show selection border. Usually drop-shadow on parent helps.
                                filter: (selectedId === panel.id && panel.clipPath !== 'none') ? 'drop-shadow(0 0 2px #9333ea)' : 'none'
                            }}
                        >
                            {/* Inner Content Label */}
                            {!panel.isBackground && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none text-xs font-mono text-gray-500 font-bold opacity-50">
                                    {panel.id}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Drawing Preview */}
                    {isDrawing && currentRect && (
                        <div
                            className="absolute border-2 border-purple-500 bg-purple-500/20"
                            style={{
                                left: currentRect.x,
                                top: currentRect.y,
                                width: currentRect.w,
                                height: currentRect.h
                            }}
                        />
                    )}

                    {/* Point Handles (Overlay on top of everything) */}
                    {mode === 'edit-points' && selectedId && (() => {
                        const panel = panels.find(p => p.id === selectedId);
                        if (!panel) return null;
                        return panel.points.map((pt, idx) => (
                            <div
                                key={idx}
                                className="absolute w-4 h-4 bg-white border-2 border-purple-600 rounded-full cursor-move z-50 hover:scale-125 transition-transform shadow-md"
                                style={{
                                    left: panel.x + (panel.width * (pt.x / 100)) - 8,
                                    top: panel.y + (panel.height * (pt.y / 100)) - 8
                                }}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    setDraggingPoint({ panelId: panel.id, pointIndex: idx });
                                }}
                            />
                        ));
                    })()}
                </div>
            </div>

            {/* Footer Actions */}
            <div className="h-16 flex items-center justify-between px-6 bg-gray-900 border-t border-gray-800 mt-4 rounded-b-lg">
                <Button variant="ghost" onClick={onCancel} className="text-gray-400 hover:text-white">
                    Cancel
                </Button>
                <div className="text-sm text-gray-500">
                    {panels.length} Panels Created
                </div>
                <Button
                    onClick={handleSave}
                    className="bg-green-600 hover:bg-green-700 text-white min-w-[140px]"
                >
                    <Check className="w-4 h-4 mr-2" /> Create Blueprint
                </Button>
            </div>
        </div>
    );
};

export default LayoutDesigner;
